import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { KeyValuesOperator } from '@/services/ws-digital/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ValidateNewItemAction } from '@/js/libs/ui/src/V2/Form/CreatableList/types';

import type { ParamsValidator } from 'src/components/RouteParamsValidator.tsx';
import type { Option } from 'src/types.ts';
import type { DigitalProductRouteParams } from './types.ts';

export const listWithCommas = (array?: string[], t?: TFunction): string | null => {
  if (array && array.length > 0) {
    return array.map((item) => (t ? t(`backend:${item}`) : item)).join(', ');
  }
  return null;
};

export const isNumberSplitByCommas = (value: string): boolean => /^\d+((,\d+)|(;\d+)|(;\s\d+)|(,\s\d+))*$/g.test(value);

export const validateNewIdItem = (v: string) => {
  const num = parseInt(v, 10);
  return !Number.isNaN(num) && num.toString() === v;
};

export const useValidateNewIdItem = () => {
  const [t] = useTranslation(['backend', 'messages']);

  return useCallbackRef<ValidateNewItemAction>((v: string) => {
    const num = parseInt(v, 10);

    if (Number.isNaN(num) || num.toString() !== v) {
      return t('messages:SHOULD_BE_NUMBER');
    }

    return undefined;
  });
};

export const isConfigEmpty = (config?: Record<string, unknown>): boolean =>
  !config ||
  Object.values(config).every((value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object' && value !== null) {
      return Object.keys(value).length === 0;
    }
    return value === null || value === undefined || value === '' || value === KeyValuesOperator.AND || value === KeyValuesOperator.OR;
  });

export const idsToStringOptions = (values?: number[]): Option<string>[] =>
  values?.map((value) => ({
    value: value.toString(),
    label: value.toString(),
  })) ?? [];

export const stringOptionsToIds = (values: Option<string>[]): number[] => values.map(({ value }) => Number(value));

export const isValidAdServerValueOption = (v: string): boolean => v.length > 0;

const NUMERIC_ID_REG_EXP = /^\d+$/;

const isOptionalNumericId = (value?: string): boolean => value === undefined || NUMERIC_ID_REG_EXP.test(value);

export const validateDigitalProductRouteIds: ParamsValidator<DigitalProductRouteParams> = ({ id, entityId }) =>
  isOptionalNumericId(id) && isOptionalNumericId(entityId);
